import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import {
  adminGetUsers,
  adminGetStats,
  adminBlockUser,
  adminChangePlan,
  adminGetPayments,
  adminGetContacts
} from '../utils/api';

export default function AdminPage() {
  const { profile } = useAuth();

  // Auth check
  if (!profile || !profile.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  // State variables
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  // Data fetching
  useEffect(() => {
    const loadData = async () => {
      try {
        setError(null);
        const [statsRes, usersRes, paymentsRes, contactsRes] = await Promise.all([
          adminGetStats(),
          adminGetUsers(),
          adminGetPayments(),
          adminGetContacts()
        ]);

        setStats(statsRes);
        setUsers(usersRes.users || []);
        setPayments(paymentsRes.payments || []);
        setContacts(contactsRes.messages || []);
      } catch (err) {
        setError(err.message || 'Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Handle block user
  const handleBlockUser = async (userId, block) => {
    setActionLoading(`block-${userId}`);
    try {
      await adminBlockUser(userId, block);
      setUsers(
        users.map(user =>
          user.id === userId ? { ...user, is_blocked: block } : user
        )
      );
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle change plan
  const handleChangePlan = async (userId, newPlan) => {
    setActionLoading(`plan-${userId}`);
    try {
      await adminChangePlan(userId, newPlan);
      setUsers(
        users.map(user =>
          user.id === userId ? { ...user, plan: newPlan } : user
        )
      );
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // Filter users based on search query
  const filteredUsers = users.filter(user =>
    (user.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center bg-zinc-900 rounded-2xl border border-white/10 p-12">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-white/10 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-blue-500">Loading admin data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Error state */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 mb-6">
            {error}
          </div>
        )}

        {/* Page title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Admin Panel</h1>
          <p className="text-gray-400">Manage your platform</p>
        </div>

        {/* Tab bar */}
        <div className="flex gap-2 border-b border-white/10 pb-4 mb-8">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: '📊' },
            { id: 'users', label: 'Users', icon: '👥' },
            { id: 'payments', label: 'Payments', icon: '💳' },
            { id: 'contacts', label: 'Messages', icon: '📩' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div>
            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {/* Total Users */}
              <div className="bg-zinc-900 rounded-2xl border border-white/10 p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-2">Total Users</p>
                    <p className="text-3xl font-bold text-white">
                      {stats?.total_users?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="bg-blue-500/10 rounded-lg p-3 text-2xl">👥</div>
                </div>
              </div>

              {/* Total Searches */}
              <div className="bg-zinc-900 rounded-2xl border border-white/10 p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-2">Total Searches</p>
                    <p className="text-3xl font-bold text-white">
                      {stats?.total_jobs?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="bg-green-500/10 rounded-lg p-3 text-2xl">🔍</div>
                </div>
              </div>

              {/* Total Leads */}
              <div className="bg-zinc-900 rounded-2xl border border-white/10 p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-2">Leads Generated</p>
                    <p className="text-3xl font-bold text-white">
                      {stats?.total_leads?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="bg-purple-500/10 rounded-lg p-3 text-2xl">📋</div>
                </div>
              </div>

              {/* Total Revenue */}
              <div className="bg-zinc-900 rounded-2xl border border-white/10 p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-2">Total Revenue</p>
                    <p className="text-3xl font-bold text-white">
                      ₹{stats?.total_revenue?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="bg-yellow-500/10 rounded-lg p-3 text-2xl">💰</div>
                </div>
              </div>
            </div>

            {/* Quick Overview */}
            <div className="bg-zinc-900 rounded-2xl border border-white/10 p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Quick Overview</h2>
              <div className="flex gap-8">
                <div>
                  <p className="text-gray-400 text-sm">
                    Users on Basic:{' '}
                    <span className="font-bold text-white">
                      {users.filter(u => u.plan === 'basic').length}
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">
                    Users on Standard:{' '}
                    <span className="font-bold text-white">
                      {users.filter(u => u.plan === 'standard').length}
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">
                    Users on Premium:{' '}
                    <span className="font-bold text-white">
                      {users.filter(u => u.plan === 'premium').length}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            {/* Search bar */}
            <div className="bg-zinc-900 rounded-2xl border border-white/10 p-6 mb-6">
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
              <p className="text-sm text-gray-400 mt-2">
                Showing {filteredUsers.length} of {users.length} users
              </p>
            </div>

            {/* Users table */}
            <div className="bg-zinc-900 rounded-2xl border border-white/10 overflow-hidden">
              {/* Header row */}
              <div className="bg-zinc-800 px-6 py-3 grid grid-cols-12 gap-4">
                <div className="col-span-3 text-xs font-medium text-gray-400 uppercase">User</div>
                <div className="col-span-2 text-xs font-medium text-gray-400 uppercase">Email</div>
                <div className="col-span-1 text-xs font-medium text-gray-400 uppercase">Plan</div>
                <div className="col-span-2 text-xs font-medium text-gray-400 uppercase">Leads Today</div>
                <div className="col-span-1 text-xs font-medium text-gray-400 uppercase">Status</div>
                <div className="col-span-3 text-xs font-medium text-gray-400 uppercase">Actions</div>
              </div>

              {/* User rows */}
              {filteredUsers.length > 0 ? (
                filteredUsers.map(user => (
                  <div
                    key={user.id}
                    className="px-6 py-4 border-t border-white/10 grid grid-cols-12 gap-4 items-center hover:bg-zinc-800/50 transition-colors"
                  >
                    {/* User */}
                    <div className="col-span-3">
                      <p className="text-white font-medium">{user.name || 'No Name'}</p>
                      <p className="text-xs text-gray-500">{user.id?.substring(0, 8)}</p>
                    </div>

                    {/* Email */}
                    <div className="col-span-2">
                      <p className="text-gray-300 text-sm truncate">{user.email}</p>
                    </div>

                    {/* Plan */}
                    <div className="col-span-1">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.plan === 'basic'
                            ? 'bg-slate-700 text-gray-300'
                            : user.plan === 'standard'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-purple-500/20 text-purple-400'
                        }`}
                      >
                        {user.plan?.charAt(0).toUpperCase() + user.plan?.slice(1)}
                      </span>
                    </div>

                    {/* Leads Today */}
                    <div className="col-span-2">
                      <p className="text-gray-300 text-sm">{user.leads_used_today || 0}</p>
                    </div>

                    {/* Status */}
                    <div className="col-span-1">
                      <div className="flex items-center">
                        <span
                          className={`w-2 h-2 rounded-full inline-block mr-1 ${
                            user.is_blocked ? 'bg-red-500' : 'bg-green-500'
                          }`}
                        ></span>
                        <span
                          className={user.is_blocked ? 'text-red-400 text-xs' : 'text-green-400 text-xs'}
                        >
                          {user.is_blocked ? 'Blocked' : 'Active'}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="col-span-3 flex gap-2">
                      {/* Block/Unblock button */}
                      <button
                        onClick={() => handleBlockUser(user.id, !user.is_blocked)}
                        disabled={actionLoading === `block-${user.id}`}
                        className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                          user.is_blocked
                            ? 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                            : 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                        } disabled:opacity-50`}
                      >
                        {actionLoading === `block-${user.id}` ? '...' : user.is_blocked ? 'Unblock' : 'Block'}
                      </button>

                      {/* Plan dropdown */}
                      <select
                        value={user.plan}
                        onChange={e => handleChangePlan(user.id, e.target.value)}
                        disabled={actionLoading === `plan-${user.id}`}
                        className="bg-zinc-800 border border-white/10 rounded-lg px-2 py-1 text-xs text-white disabled:opacity-50"
                      >
                        <option value="basic">basic</option>
                        <option value="standard">standard</option>
                        <option value="premium">premium</option>
                      </select>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-6 py-12 text-center">
                  <p className="text-gray-500">No users found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div>
            <div className="bg-zinc-900 rounded-2xl border border-white/10 overflow-hidden">
              {/* Summary bar */}
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <h2 className="text-lg font-semibold text-white">Payment History</h2>
                <p className="text-sm text-gray-400">Total: {payments.length} payments</p>
              </div>

              {payments.length > 0 ? (
                <div>
                  {/* Header row */}
                  <div className="bg-zinc-800 px-6 py-3 grid grid-cols-12 gap-4 hidden sm:grid">
                    <div className="col-span-3 text-xs font-medium text-gray-400 uppercase">Order ID</div>
                    <div className="col-span-2 text-xs font-medium text-gray-400 uppercase">Payment ID</div>
                    <div className="col-span-2 text-xs font-medium text-gray-400 uppercase">Plan</div>
                    <div className="col-span-2 text-xs font-medium text-gray-400 uppercase">Amount</div>
                    <div className="col-span-1 text-xs font-medium text-gray-400 uppercase">Status</div>
                    <div className="col-span-2 text-xs font-medium text-gray-400 uppercase">Date</div>
                  </div>

                  {/* Payment rows */}
                  {payments.map((payment, idx) => (
                    <div
                      key={idx}
                      className="px-6 py-4 border-t border-white/10 grid grid-cols-12 gap-4 items-center"
                    >
                      {/* Order ID */}
                      <div className="col-span-3">
                        <p className="text-gray-300 text-sm truncate overflow-hidden text-ellipsis">
                          {payment.razorpay_order_id}
                        </p>
                      </div>

                      {/* Payment ID */}
                      <div className="col-span-2">
                        <p className="text-gray-300 text-sm truncate overflow-hidden text-ellipsis">
                          {payment.razorpay_payment_id || '—'}
                        </p>
                      </div>

                      {/* Plan */}
                      <div className="col-span-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            payment.plan === 'basic'
                              ? 'bg-slate-700 text-gray-300'
                              : payment.plan === 'standard'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-purple-500/20 text-purple-400'
                          }`}
                        >
                          {payment.plan?.charAt(0).toUpperCase() + payment.plan?.slice(1)}
                        </span>
                      </div>

                      {/* Amount */}
                      <div className="col-span-2">
                        <p className="text-white font-medium">
                          ₹{((payment.amount || 0) / 100).toLocaleString()}
                        </p>
                      </div>

                      {/* Status */}
                      <div className="col-span-1">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            payment.status === 'completed'
                              ? 'bg-green-500/20 text-green-400'
                              : payment.status === 'created'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {payment.status?.charAt(0).toUpperCase() + payment.status?.slice(1)}
                        </span>
                      </div>

                      {/* Date */}
                      <div className="col-span-2">
                        <p className="text-gray-400 text-sm">
                          {payment.created_at
                            ? new Date(payment.created_at).toLocaleDateString('en-IN')
                            : '—'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-6 py-12 text-center">
                  <div className="text-4xl mb-3">💳</div>
                  <h4 className="text-lg font-semibold text-white mb-2">No Payments Yet</h4>
                  <p className="text-gray-400">Payment history will appear here.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div>
            <div className="bg-zinc-900 rounded-2xl border border-white/10 overflow-hidden">
              {/* Summary bar */}
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <h2 className="text-lg font-semibold text-white">Contact Messages</h2>
                <p className="text-sm text-gray-400">Total: {contacts.length} messages</p>
              </div>

              {contacts.length > 0 ? (
                <div>
                  {contacts.map((contact, idx) => (
                    <div
                      key={idx}
                      className="border-t border-white/10 p-6 hover:bg-zinc-800/30 transition-colors"
                    >
                      {/* Top row */}
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="text-white font-medium">{contact.name}</p>
                          <p className="text-sm text-blue-400">{contact.email}</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          {contact.created_at
                            ? new Date(contact.created_at).toLocaleDateString('en-IN')
                            : '—'}
                        </p>
                      </div>

                      {/* Message body */}
                      <p className="text-gray-300 text-sm leading-relaxed">{contact.message}</p>

                      {/* Read status badge */}
                      <div className="mt-2">
                        {contact.is_read ? (
                          <span className="bg-green-500/10 text-green-400 text-xs px-2 py-0.5 rounded">
                            Read
                          </span>
                        ) : (
                          <span className="bg-yellow-500/10 text-yellow-400 text-xs px-2 py-0.5 rounded">
                            Unread
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-6 py-12 text-center">
                  <div className="text-4xl mb-3">📭</div>
                  <h4 className="text-lg font-semibold text-white mb-2">No Messages Yet</h4>
                  <p className="text-gray-400">Contact form messages will appear here.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
